package com.patronat.top.core.network

import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.dto.AuthActionResponse
import com.patronat.top.dto.AuthResponse
import com.patronat.top.dto.AuthSuccessResponse
import com.patronat.top.dto.RefreshTokenRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.compression.ContentEncoding
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.plugins.logging.SIMPLE
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.encodedPath
import io.ktor.http.takeFrom
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

object HttpClientFactory {

    fun create(
        tokenStorage: TokenStorage,
        baseUrl: String,
        enableLogging: Boolean = true
    ): HttpClient {
        return HttpClient {

            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    prettyPrint = true
                    isLenient = true
                    encodeDefaults = true
                    classDiscriminator = "type"
                })
            }

            if (enableLogging) {
                install(Logging) {
                    level = LogLevel.ALL
                    logger = Logger.SIMPLE
                    sanitizeHeader { header -> header == HttpHeaders.Authorization }
                }
            }

            install(HttpTimeout) {
                requestTimeoutMillis = 30_000
                connectTimeoutMillis = 30_000
                socketTimeoutMillis = 30_000
            }

            install(ContentEncoding) {
                gzip()
                deflate()
            }

            defaultRequest {
                val fixedBaseUrl = if (baseUrl.endsWith("/")) baseUrl else "$baseUrl/"
                url.takeFrom(fixedBaseUrl)
                contentType(ContentType.Application.Json)
            }

            install(Auth) {
                bearer {
                    loadTokens {
                        val accessToken = tokenStorage.getAccessToken()
                        val refreshToken = tokenStorage.getRefreshToken()

                        if (accessToken != null && refreshToken != null) {
                            BearerTokens(accessToken, refreshToken)
                        } else {
                            null
                        }
                    }

                    refreshTokens {
                        val oldRefreshToken = oldTokens?.refreshToken ?: return@refreshTokens null

                        try {
                            val response = client.post("auth/refresh") {
                                setBody(RefreshTokenRequest(oldRefreshToken))
                                markAsRefreshTokenRequest()
                            }

                            if (response.status == HttpStatusCode.OK) {
                                val authResponse = response.body<AuthResponse>()

                                if (authResponse is AuthSuccessResponse) {
                                    tokenStorage.saveTokens(authResponse.accessToken, authResponse.refreshToken, authResponse.userRole)
                                    BearerTokens(authResponse.accessToken, authResponse.refreshToken)
                                } else {
                                    tokenStorage.clear()
                                    null
                                }
                            } else {
                                tokenStorage.clear()
                                null
                            }
                        } catch (e: Exception) {
                            tokenStorage.clear()
                            null
                        }
                    }

                    sendWithoutRequest { request ->
                        val path = request.url.encodedPath
                        !path.contains("/auth")
                    }
                }
            }
        }
    }
}